import { AsyncPipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, Observable, switchMap, withLatestFrom } from 'rxjs';

import {
  CanvasElement,
  CanvasElementId,
  getNamespaceId,
  SlotIdOf,
} from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import { getBackground, makeContainerVisualEffectStyles } from '@blaze-shared/elements-domain';

import { getElementById, getElements } from '../../+state/elements/elements.selectors';
import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { SlotNodeDirective } from '../../canvas-elements/rendering/slot-node.directive';
import { CloseAffordanceRegistry } from '../../services/close-affordance-registry.service';
import { DialogOverlayService } from '../../services/dialog/dialog-overlay.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

function hasSlotChildren(
  elementId: CanvasElementId | undefined,
  elements: CanvasElement[],
  slotId: string,
): boolean {
  return elementId
    ? elements.some((el) => el.parentNode?.id === elementId && el.parentNode?.slot === slotId)
    : true;
}

@UntilDestroy()
@Component({
  selector: 'be-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgStyle, SlotNodeDirective, NgTemplateOutlet, MatIcon],
})
export class DialogComponent
  extends CanvasElementBaseComponent<'dialog'>
  implements CanvasElementComponent<'dialog'>, OnInit, OnChanges
{
  private readonly closeAffordance = inject(CloseAffordanceRegistry);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogOverlay = inject(DialogOverlayService);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  private readonly closeButton = viewChild<ElementRef<HTMLElement>>('closeButton');

  @HostBinding('class.full-w') isFullWidth = false;
  @HostBinding('style') dialogStyle: Record<string, string> = {};

  isHeaderEmpty$: Observable<boolean>;
  isFooterEmpty$: Observable<boolean>;
  headerStyle!: Record<string, string>;
  contentStyle!: Record<string, string>;
  footerStyle!: Record<string, string>;
  isCloseButtonHidden = signal(false);

  constructor() {
    super();
    const elements$ = this.elementId$.pipe(withLatestFrom(this.store.select(getElements)));

    this.isHeaderEmpty$ = elements$.pipe(
      map(([elementId, elements]) => !hasSlotChildren(elementId, elements, 'header')),
    );
    this.isFooterEmpty$ = elements$.pipe(
      map(([elementId, elements]) => !hasSlotChildren(elementId, elements, 'footer')),
    );
    this.elementId$
      .pipe(
        filter(Boolean),
        switchMap((elementId) => this.store.select(getElementById(elementId))),
        untilDestroyed(this),
      )
      .subscribe((element) => {
        this.isFullWidth = element?.width?.mode === 'full';
      });

    // The X overlaps the content slot only when it is rendered and no header sits above it —
    // the same predicate the template uses for the content's `close-affordance` class.
    const isHeaderEmpty = toSignal(this.isHeaderEmpty$);
    const overlappingCloseButton = computed(() =>
      !this.isCloseButtonHidden() && isHeaderEmpty()
        ? (this.closeButton()?.nativeElement ?? null)
        : null,
    );
    this.destroyRef.onDestroy(
      this.closeAffordance.register(this.elementRef.nativeElement, overlappingCloseButton),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['props']) {
      this.isCloseButtonHidden.set(!!this.props?.content.options.hideCloseButton);
    }
    if (changes['slots'] || changes['props']) {
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.dialogOverlay.adjustOverlayDisplacementOnInit();
  }

  closeDialog(event: MouseEvent): void {
    // ignore non-left mouse button clicks
    if (event.button !== 0) {
      return;
    }
    markEventAsHandled(event);
    if (this.elementId) {
      this.dialogOverlay.closeDialog(getNamespaceId(this.elementId));
    }
  }

  private updateStyles(): void {
    if (this.props) {
      const { container } = this.props.style ?? {};
      const dialogStyle: Record<string, string> = {
        ...(container?.visualEffect
          ? makeContainerVisualEffectStyles(container.visualEffect)
          : undefined),
        padding: container?.offset.padding ?? '0px',
      };
      if (container?.outline.color1) {
        dialogStyle['border-color'] = container.outline.color1;
      }
      if (container?.outlineWidth) {
        dialogStyle['border-width'] = container.outlineWidth;
      }
      if (container?.radius) {
        dialogStyle['--dialog-radius'] = container.radius;
      }
      if (container?.fill && container?.fill.selectorType !== 'none') {
        dialogStyle['--dialog-bg'] = getBackground(container?.fill);
      }

      this.dialogStyle = dialogStyle;
    }
    if (this.slots) {
      this.headerStyle = this.getSectionStyle('header');
      this.contentStyle = this.getSectionStyle('content');
      if (this.props?.style?.container?.radius) {
        this.contentStyle['border-radius'] = this.props.style.container.radius;
      }
      this.footerStyle = this.getSectionStyle('footer');
    }
  }

  private getSectionStyle(slot: SlotIdOf<'dialog'>): Record<string, string> {
    const style = (this.slots?.slotNodesData[slot]?.props as any)?.style;
    return { padding: style?.padding };
  }
}
